import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import api from "utils/api";

interface UploadProps {
  uploadType: string;
  details: Record<string, unknown>;
  fileType?: string;
  onSuccess?: () => void;
}

const Upload: React.FC<UploadProps> = ({ uploadType, details, fileType = "text/csv", onSuccess }) => {
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadFile = useMutation({
    mutationFn: async (file: File) => {
      setStatus("Uploading...");
      setError(null);
      try {
        // Create upload file
        const uploadFile = await api.post(`/upload/`, {
          upload_type: uploadType,
          file_type: fileType,
          name: file.name,
        });
        const { id } = uploadFile.data as { id: string };
        const form = await api.get(`/upload/${id}/form/`, {});
        const { url, fields } = form.data as { url: string; fields: Record<string, string> };
        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => {
          formData.append(key, value);
        });
        formData.append("file", file);
        const uploadResp = await fetch(url, {
          method: "POST",
          body: formData,
        });
        if (uploadResp.ok) {
          setStatus("Processing...");
          await api.post(`/upload/${id}/process/`, {
            ...details,
          });
          setStatus(null);
          onSuccess && onSuccess();
          inputRef.current && (inputRef.current.value = "");
          return;
        } else {
          setStatus(null);
          setError("An error occurred");
        }
      } catch {
        setStatus(null);
        setError("An error occurred");
      }
      inputRef.current && (inputRef.current.value = "");
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file) {
        if (file.type !== fileType) {
          setError("Invalid file type");
          return;
        }
        uploadFile.mutate(file);
      }
    }
  };

  return (
    <div>
      <input type="file" ref={inputRef} accept={fileType} disabled={uploadFile.isPending} onChange={handleChange} />
      {status && <p>{status}</p>}
      {error && <p>{error}</p>}
    </div>
  );
};

export default Upload;
